import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import {
  buildBreadcrumbSchema,
  buildFaqSchema,
  buildHowToStepsSchema,
  buildSpeakableSchema,
} from "@/lib/schema";
import { POA_FAQ_CATEGORIES } from "@/content/poa-corpus/faq-defs";
import { POA_JURISDICTION_NAMES as POA_JURISDICTIONS } from "@/content/poa-corpus/names.gen";

const STEPS = [
  {
    title: "กำหนดขอบเขตอำนาจให้ตรงกับธุรกรรมจริง",
    body: "ระบุชื่อผู้มอบ ผู้รับมอบ เลขที่เอกสารทรัพย์สินหรือบัญชี และการกระทำที่อนุญาตอย่างเจาะจง เพราะหน่วยงานไทยและต่างประเทศตีความหนังสือมอบอำนาจอย่างเคร่งครัดตาม ป.พ.พ. มาตรา 797–828",
  },
  {
    title: "เลือกแบบฟอร์มที่หน่วยงานปลายทางบังคับ",
    body: "ธุรกรรมที่ดินต้องใช้แบบ ท.ด.21 ของกรมที่ดิน งานนิติบุคคลใช้แบบที่กรมพัฒนาธุรกิจการค้ากำหนด ส่วนธนาคารและกองมรดกมักมีแบบเฉพาะของตัวเอง ตรวจก่อนเซ็นเสมอ",
  },
  {
    title: "ลงนามต่อหน้าทนายความผู้ทำคำรับรองลายมือชื่อ",
    body: "ทนายความที่ขึ้นทะเบียน Notarial Services Attorney กับสภาทนายความรับรองลายมือชื่อ ตัวตนผู้ลงนาม และความสมัครใจ พร้อมบันทึกในสมุดทะเบียนที่ตรวจย้อนหลังได้",
  },
  {
    title: "ติดอากรแสตมป์และแปลรับรองหากใช้ข้ามประเทศ",
    body: "ใบมอบอำนาจต้องติดอากรแสตมป์ตามบัญชีอัตราของกรมสรรพากร และถ้าใช้ต่างประเทศต้องแปลรับรองตามรูปแบบที่ผู้รับปลายทางกำหนดก่อนเข้าขั้นนิติกรณ์",
  },
  {
    title: "รับรองกรมการกงสุลและสถานทูตปลายทางหรือ Apostille",
    body: "ยื่นนิติกรณ์ที่กองสัญชาติและนิติกรณ์ 200 บาทต่อตราแบบปกติหรือ 400 บาทแบบด่วน แล้วรับรองสถานทูตปลายทางอีกชั้น ตั้งแต่ 28 กุมภาพันธ์ 2570 ประเทศภาคีอนุสัญญากรุงเฮกใช้ Apostille ใบเดียวแทนได้",
  },
];

export const Route = createFileRoute("/poa/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/poa-corpus/faq-th");
    return { faqs: m.POA_FAQ_TH.slice(0, 12) };
  },
  component: PoaHub,
  head: ({ loaderData }) => {
    const site = getActiveSiteUrl();
    const faqs = loaderData?.faqs ?? [];
    return {
      ...pageHeadForProvince({
        title: "หนังสือมอบอำนาจข้ามประเทศ — แบบฟอร์ม รับรองลายมือชื่อ เขตอำนาจปลายทาง",
        description:
          "ศูนย์รวมหนังสือมอบอำนาจ (Power of Attorney) สำหรับที่ดิน ธนาคาร มรดก และนิติบุคคล: แบบฟอร์มที่หน่วยงานบังคับ อากรแสตมป์ ลำดับรับรองกรมการกงสุล–สถานทูต และเส้นทาง Apostille ปี 2570",
        path: "/poa",
        alternates: [{ lang: "en", path: "/en/poa" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "หนังสือมอบอำนาจ", url: `${site}/poa` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildHowToStepsSchema({
              name: "ขั้นตอนจัดทำหนังสือมอบอำนาจให้ใช้ได้ทั้งในไทยและต่างประเทศ",
              description: "ลำดับงานจริงจากร่างขอบเขตอำนาจถึงรับรองสถานทูตปลายทาง",
              path: "/poa",
              steps: STEPS.map((s, i) => ({ step: i + 1, title: s.title, body: s.body })),
            }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "หนังสือมอบอำนาจตามเขตอำนาจปลายทาง",
            itemListElement: POA_JURISDICTIONS.map((j, i) => ({
              "@type": "ListItem",
              position: i + 1,
              url: `${site}/poa/jurisdiction/${j.slug}`,
              name: j.nameTh,
            })),
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/poa")) },
      ],
    };
  },
});

function PoaHub() {
  const { faqs } = Route.useLoaderData();
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="main-content" className="container mx-auto max-w-5xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span> <span>หนังสือมอบอำนาจ</span>
        </nav>
        <h1 className="text-3xl font-extrabold md:text-4xl">
          หนังสือมอบอำนาจ (Power of Attorney) — แบบฟอร์ม การรับรอง และเขตอำนาจปลายทาง
        </h1>
        <p className="mt-4 max-w-3xl text-muted-foreground" data-speakable="true">
          หนังสือมอบอำนาจที่ใช้ได้จริงต้องระบุขอบเขตอำนาจชัดเจน ใช้แบบฟอร์มที่หน่วยงานปลายทางบังคับ เช่น ท.ด.21
          ของกรมที่ดิน ติดอากรแสตมป์ตามอัตราของกรมสรรพากร และลงนามต่อหน้าทนายความผู้ทำคำรับรองลายมือชื่อ
          หากนำไปใช้ต่างประเทศต้องผ่านนิติกรณ์กรมการกงสุล 200 บาทต่อตราแบบปกติหรือ 400 บาทแบบด่วน แล้วรับรองสถานทูตปลายทางอีกชั้น
        </p>

        <section className="mt-10">
          <h2 className="mb-4 text-xl font-bold">ขั้นตอนการทำงาน 5 ขั้น</h2>
          <ol className="space-y-3">
            {STEPS.map((s, i) => (
              <li key={s.title} id={`step-${i + 1}`} className="rounded-lg border bg-card p-4">
                <div className="font-semibold">{i + 1}. {s.title}</div>
                <p className="mt-1 text-sm text-muted-foreground">{s.body}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-10">
          <h2 className="mb-3 text-xl font-bold">หัวข้อความรู้และคำถามที่พบบ่อย</h2>
          <ul className="grid gap-3 sm:grid-cols-2">
            {POA_FAQ_CATEGORIES.map((c) => (
              <li key={c.slug} className="rounded-lg border bg-card p-4">
                <a href={`/poa/faq/${c.slug}`} className="font-semibold hover:underline">{c.titleTh}</a>
                <p className="mt-1 text-sm text-muted-foreground">{c.descTh}</p>
              </li>
            ))}
          </ul>
          <a href="/poa/faq" className="mt-4 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">
            ดูคำถาม-คำตอบทั้งหมด
          </a>
        </section>

        <section className="mt-10">
          <h2 className="mb-3 text-xl font-bold">หนังสือมอบอำนาจตามเขตอำนาจปลายทาง</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {POA_JURISDICTIONS.map((j) => (
              <a key={j.slug} href={`/poa/jurisdiction/${j.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {j.nameTh}
              </a>
            ))}
          </div>
        </section>

        <section className="mt-10 space-y-3">
          <h2 className="text-xl font-bold">คำถามที่พบบ่อย</h2>
          {faqs.map((f: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 4 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">{f.q}</summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </section>

        <section className="mt-10 rounded-lg border bg-card p-5">
          <h2 className="mb-2 text-lg font-bold">ให้ทีมร่างและตรวจหนังสือมอบอำนาจก่อนเซ็น</h2>
          <p className="text-sm text-muted-foreground">
            แจ้งประเภทธุรกรรม หน่วยงานผู้รับ และประเทศปลายทาง — ทีมตรวจแบบฟอร์มและลำดับรับรองให้ก่อนลงนาม
          </p>
          <a href="/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">ติดต่อทีมงาน</a>
        </section>
        <FaqSourceNote topic="poa" locale="th" />
      </main>
      <SiteFooter />
    </div>
  );
}
